import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_SERIES,
  seriesColor,
  STATUS_COLORS,
  OTHER_COLOR,
} from "../shared/chartTheme";
import {
  fetchInventoryRevisionsReport,
  selectInventoryRevisionsReportData,
  selectInventoryRevisionsReportError,
  selectInventoryRevisionsReportStatus,
} from "../features/reports/reportsSlice";
import {
  fetchStores,
  selectStores,
  selectStoresStatus,
} from "../features/stores/storesSlice";
import {
  INVENTORY_DIFF_REASON_OPTIONS,
  INVENTORY_REVISION_STATUS_LABELS,
  inventoryDiffReasonLabel,
  inventoryRevisionStatusLabel,
} from "../shared/inventoryRevisionEnums";
import { moneyRu } from "../shared/inventoryRevisionPostedFinance";
import { Select } from "../components/Select";
import { useIsMobile } from "../shared/useMediaQuery";
import "./WarehouseRevisionReportsPage.css";

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function getDefaultDateRange() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return { startDate: toIsoLocal(first), endDate: toIsoLocal(today) };
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

const PIE_COLORS = CHART_SERIES;
const REASON_CHART_COLORS = {
  loss_theft: STATUS_COLORS.danger,
  loss_damage: seriesColor(1),
  loss_expired: STATUS_COLORS.warning,
  loss_accounting_error: seriesColor(4),
  surplus_return: STATUS_COLORS.success,
  other: OTHER_COLOR,
  unknown: OTHER_COLOR,
};

const STATUS_OPTIONS = [
  { value: "all", label: "Все статусы" },
  ...Object.entries(INVENTORY_REVISION_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

/**
 * Подпись доли внутри сектора.
 *
 * recharts по умолчанию выносит подписи за круг, и на узком экране они
 * вылезают за карточку. Считаем координаты сами и ставим текст в
 * середину сектора; название категории читается в легенде.
 */
const RAD = Math.PI / 180;
function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.08) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text
      x={x}
      y={y}
      fill="var(--c-on-series)"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={700}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}
export default function WarehouseRevisionReportsPage() {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const data = useSelector(selectInventoryRevisionsReportData);
  const status = useSelector(selectInventoryRevisionsReportStatus);
  const error = useSelector(selectInventoryRevisionsReportError);
  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const defaultRange = useMemo(getDefaultDateRange, []);
  const [storeId, setStoreId] = useState(searchParams.get("store_id") || "");
  const [revisionStatus, setRevisionStatus] = useState(searchParams.get("status") || "all");
  const [diffReason, setDiffReason] = useState(searchParams.get("diff_reason") || "");
  const [dateField, setDateField] = useState(searchParams.get("date_field") || "created_at");
  const [startDate, setStartDate] = useState(
    searchParams.get("start_date") || defaultRange.startDate
  );
  const [endDate, setEndDate] = useState(searchParams.get("end_date") || defaultRange.endDate);

  useEffect(() => {
    if (storesStatus === "idle") dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch, storesStatus]);

  const loadReport = () => {
    dispatch(
      fetchInventoryRevisionsReport({
        storeId: storeId || undefined,
        startDate,
        endDate,
        status: revisionStatus,
        diffReason: diffReason || undefined,
        dateField,
      })
    );
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    const next = new URLSearchParams();
    if (storeId) next.set("store_id", storeId);
    if (revisionStatus && revisionStatus !== "all") next.set("status", revisionStatus);
    if (diffReason) next.set("diff_reason", diffReason);
    if (dateField && dateField !== "created_at") next.set("date_field", dateField);
    if (startDate) next.set("start_date", startDate);
    if (endDate) next.set("end_date", endDate);
    setSearchParams(next, { replace: true });
    loadReport();
  };

  const handleReset = () => {
    const range = getDefaultDateRange();
    setStoreId("");
    setRevisionStatus("all");
    setDiffReason("");
    setDateField("created_at");
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setSearchParams(new URLSearchParams(), { replace: true });
    dispatch(
      fetchInventoryRevisionsReport({
        startDate: range.startDate,
        endDate: range.endDate,
        status: "all",
        dateField: "created_at",
      })
    );
  };

  const summary = data?.summary || {};
  const finance = summary.finance || {};
  const byStatus = data?.by_status || [];
  const byDiffReason = data?.by_diff_reason || [];
  const byStore = data?.by_store || [];
  const daily = data?.daily || [];
  const revisions = data?.revisions || [];
  const topTheft = data?.top_theft_products || [];

  const reasonPieData = useMemo(
    () =>
      byDiffReason.map((row) => ({
        name: row.diff_reason_label || inventoryDiffReasonLabel(row.diff_reason),
        value: Number(row.amount_cost || 0),
        reason: row.diff_reason,
      })),
    [byDiffReason]
  );

  const statusPieData = useMemo(
    () =>
      byStatus.map((row) => ({
        name: row.status_label || inventoryRevisionStatusLabel(row.status),
        value: row.count || 0,
      })),
    [byStatus]
  );

  const shortageSurplusBar = useMemo(
    () => [
      {
        name: "Недостача",
        cost: Number(finance.shortage_cost || 0),
        retail: Number(finance.shortage_retail || 0),
        wholesale: Number(finance.shortage_wholesale || 0),
      },
      {
        name: "Излишек",
        cost: Number(finance.surplus_cost || 0),
        retail: Number(finance.surplus_retail || 0),
        wholesale: Number(finance.surplus_wholesale || 0),
      },
    ],
    [finance]
  );

  const dailyChartData = useMemo(
    () =>
      daily.map((row) => ({
        date: row.date?.slice(5) || row.date,
        shortage: Number(row.shortage_cost || 0),
        surplus: Number(row.surplus_cost || 0),
        revisions: row.revisions_count || 0,
      })),
    [daily]
  );

  const storeBarData = useMemo(
    () =>
      byStore.map((row) => ({
        name: row.store_name?.length > 18 ? `${row.store_name.slice(0, 16)}…` : row.store_name,
        shortage: Number(row.shortage_cost || 0),
        surplus: Number(row.surplus_cost || 0),
      })),
    [byStore]
  );

  const theftBarData = useMemo(
    () =>
      topTheft.map((row) => ({
        name:
          row.product_name?.length > 28
            ? `${row.product_name.slice(0, 26)}…`
            : row.product_name || `#${row.product_id}`,
        amount: Number(row.amount_cost || 0),
      })),
    [topTheft]
  );

  const periodLabel = data?.period
    ? `${data.period.start_date} — ${data.period.end_date}`
    : "";

  return (
    <div className="revision-reports-page" style={styles.page}>
      <header className="page-header" style={styles.header}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.title}>Отчёт по ревизиям</div>
            <div style={styles.subtitle}>
              Все ревизии за период · финансы и причины расхождений по опубликованным
              {periodLabel ? ` · ${periodLabel}` : ""}
            </div>
          </div>
          <Link to="/warehouse/revision" style={styles.backLink}>
            ← К списку ревизий
          </Link>
        </div>
      </header>

      <div className="toolbar" style={styles.toolbar}>
        <div style={styles.field}>
          <label style={styles.label}>Магазин</label>
          <Select
            value={storeId}
            onChange={setStoreId}
            options={[
              { value: "", label: "Все магазины" },
              ...stores.map((st) => ({
                value: String(st.id),
                label: st.name || `Магазин #${st.id}`,
              })),
            ]}
            placeholder="Все магазины"
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Статус ревизии</label>
          <Select
            value={revisionStatus}
            onChange={setRevisionStatus}
            options={STATUS_OPTIONS}
            isSearchable={false}
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Причина расхождения</label>
          <Select
            value={diffReason}
            onChange={setDiffReason}
            options={[
              { value: "", label: "Все причины" },
              ...INVENTORY_DIFF_REASON_OPTIONS,
            ]}
            placeholder="Все причины"
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Дата по полю</label>
          <Select
            value={dateField}
            onChange={setDateField}
            options={[
              { value: "created_at", label: "Создания" },
              { value: "posted_at", label: "Публикации" },
            ]}
            isSearchable={false}
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>С даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            customInput={<input style={styles.input} />}
          />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>По дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            customInput={<input style={styles.input} />}
          />
        </div>
        <div style={styles.toolbarButtons}>
          <button type="button" onClick={handleApply} style={styles.applyBtn}>
            Обновить
          </button>
          <button type="button" onClick={handleReset} style={styles.resetBtn}>
            Сбросить
          </button>
        </div>
      </div>

      {status === "loading" && <Loading text="Загрузка отчёта…" />}
      {status === "failed" && <div style={styles.error}>{error || "Ошибка загрузки"}</div>}

      <div className="kpi-grid">
        <KpiCard title="Ревизий за период" value={String(summary.revisions_count || 0)} />
        <KpiCard title="Опубликовано" value={String(summary.posted_count || 0)} accent="var(--c-success)" />
        <KpiCard
          title="Недостача (себест.)"
          value={moneyRu(finance.shortage_cost)}
          accent="var(--c-danger)"
        />
        <KpiCard
          title="Недостача (розница)"
          value={moneyRu(finance.shortage_retail)}
          accent="var(--c-warning)"
        />
        <KpiCard title="Излишек (себест.)" value={moneyRu(finance.surplus_cost)} accent="var(--c-s1)" />
        <KpiCard
          title="Строк с расхождением"
          value={`${finance.shortage_lines || 0} / ${finance.surplus_lines || 0}`}
          hint="недостача / излишек"
        />
      </div>

      <div className="charts-grid">
        <ChartCard title="Недостача по причинам (себестоимость)">
          {reasonPieData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={reasonPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="78%"
                  labelLine={false}
                  label={renderPieLabel}
                >
                  {reasonPieData.map((entry, index) => (
                    <Cell
                      key={`reason-${entry.reason}`}
                      fill={
                        REASON_CHART_COLORS[entry.reason] ||
                        PIE_COLORS[index % PIE_COLORS.length]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => moneyRu(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.emptyChart}>Нет данных по причинам</div>
          )}
        </ChartCard>

        <ChartCard title="Ревизии по статусам">
          {statusPieData.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="78%"
                  labelLine={false}
                  label={renderPieLabel}
                >
                  {statusPieData.map((entry, index) => (
                    <Cell key={`status-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.emptyChart}>Нет ревизий за период</div>
          )}
        </ChartCard>

        <ChartCard title="Недостача vs излишек (опубликованные)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={shortageSurplusBar}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis dataKey="name" stroke="var(--c-text-dim)" />
              <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
              <Tooltip formatter={(v) => moneyRu(v)} />
              <Legend />
              <Bar dataKey="cost" name="Себестоимость" fill="var(--c-danger)" />
              <Bar dataKey="retail" name="Розница" fill={seriesColor(1)} />
              <Bar dataKey="wholesale" name="Опт" fill={seriesColor(4)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Динамика публикаций и убытков">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis dataKey="date" stroke="var(--c-text-dim)" />
              <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
              <Tooltip formatter={(v, name) => (name === "revisions" ? v : moneyRu(v))} />
              <Legend />
              <Area
                type="monotone"
                dataKey="shortage"
                name="Недостача"
                stroke="var(--c-danger)"
                fill="color-mix(in srgb, var(--c-danger) 20%, transparent)"
              />
              <Area
                type="monotone"
                dataKey="surplus"
                name="Излишек"
                stroke="var(--c-success)"
                fill="color-mix(in srgb, var(--c-success) 13%, transparent)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {storeBarData.length > 0 ? (
          <ChartCard title="По магазинам: недостача и излишек">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={storeBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis dataKey="name" stroke="var(--c-text-dim)" />
                <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
                <Tooltip formatter={(v) => moneyRu(v)} />
                <Legend />
                <Bar dataKey="shortage" name="Недостача" fill="var(--c-danger)" />
                <Bar dataKey="surplus" name="Излишек" fill="var(--c-success)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        ) : null}

        {theftBarData.length > 0 ? (
          <ChartCard title="Топ товаров: кража (loss_theft)">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={theftBarData} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis type="number" tickFormatter={formatAxisMoney} stroke="var(--c-text-dim)" />
                {/* 200px под названия товаров съедают больше половины экрана
                    телефона — на узком отдаём осям минимум. */}
                <YAxis
                  type="category"
                  dataKey="name"
                  width={isMobile ? 96 : 200}
                  tick={{ fontSize: isMobile ? 11 : 13 }}
                  stroke="var(--c-text-dim)"
                />
                <Tooltip formatter={(v) => moneyRu(v)} />
                <Bar dataKey="amount" name="Сумма (себест.)" fill="var(--c-danger)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        ) : null}
      </div>

      <Section title="Причины недостачи (опубликованные)">
        <SimpleTable
          rows={byDiffReason}
          columns={[
            { key: "diff_reason_label", label: "Причина" },
            { key: "lines_count", label: "Строк" },
            { key: "qty", label: "Кол-во" },
            { key: "amount_cost", label: "Себест.", render: moneyRu },
            { key: "amount_retail", label: "Розница", render: moneyRu },
            { key: "amount_wholesale", label: "Опт", render: moneyRu },
          ]}
          emptyText="Нет расхождений"
        />
      </Section>

      <Section title="По магазинам (опубликованные)">
        <SimpleTable
          rows={byStore}
          columns={[
            { key: "store_name", label: "Магазин" },
            { key: "posted_revisions_count", label: "Ревизий" },
            { key: "shortage_lines", label: "Строк недост." },
            { key: "surplus_lines", label: "Строк излиш." },
            { key: "shortage_cost", label: "Недостача", render: moneyRu },
            { key: "surplus_cost", label: "Излишек", render: moneyRu },
          ]}
          emptyText="Нет опубликованных ревизий"
        />
      </Section>

      <Section title="Все ревизии за период">
        <SimpleTable
          rows={revisions}
          columns={[
            {
              key: "id",
              label: "№",
              render: (v, row) => (
                <Link to={`/warehouse/revision/${row.id}`} style={styles.link}>
                  #{v}
                </Link>
              ),
            },
            { key: "store_name", label: "Магазин" },
            { key: "status_label", label: "Статус" },
            { key: "items_count", label: "Позиций" },
            { key: "created_at", label: "Создана", render: formatDateTime },
            { key: "posted_at", label: "Опубликована", render: formatDateTime },
            {
              key: "finance",
              label: "Недостача",
              render: (fin) => (fin ? moneyRu(fin.shortage_cost) : "—"),
            },
            {
              key: "finance",
              label: "Излишек",
              render: (fin, row) =>
                fin ? (
                  moneyRu(fin.surplus_cost)
                ) : row.status === "posted" ? (
                  "0,00"
                ) : (
                  "—"
                ),
            },
            {
              key: "id",
              label: "",
              render: (id, row) =>
                row.status === "posted" ? (
                  <Link to={`/warehouse/revision/${id}/finance/shortage`} style={styles.linkSmall}>
                    Финансы →
                  </Link>
                ) : (
                  "—"
                ),
            },
          ]}
          emptyText="Нет ревизий"
        />
      </Section>
    </div>
  );
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div style={styles.kpiCard}>
      <div style={styles.kpiTitle}>{title}</div>
      <div style={{ ...styles.kpiValue, color: accent || "var(--c-text)" }}>{value}</div>
      {hint ? <div style={styles.kpiHint}>{hint}</div> : null}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="section" style={styles.section}>
      <div style={styles.sectionTitle}>{title}</div>
      <div>{children}</div>
    </section>
  );
}

function ChartCard({ title, children }) {
  return (
    <section className="chart-card" style={styles.chartCard}>
      <div style={styles.chartTitle}>{title}</div>
      <div style={styles.chartBody}>{children}</div>
    </section>
  );
}

function SimpleTable({ rows, columns, emptyText }) {
  if (!rows.length) return <div style={styles.empty}>{emptyText}</div>;
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={`${c.key}-${c.label}-${i}`} style={styles.th}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id ?? `${idx}-${row.date || row.store_id}`}>
              {columns.map((c, ci) => (
                <td key={`${c.key}-${ci}`} style={styles.td}>
                  {c.render ? c.render(row[c.key], row) : row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100%",
    background: "var(--c-bg)",
    color: "var(--c-text)",
    display: "flex",
    flexDirection: "column",
    paddingBottom: 32,
  },
  header: {
    padding: "18px 24px 10px",
    borderBottom: "1px solid var(--c-border)",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  title: { fontSize: 22, fontWeight: 700, color: "var(--c-text)" },
  subtitle: { marginTop: 4, fontSize: 14, color: "var(--c-text-dim)", maxWidth: 640 },
  backLink: {
    fontSize: 15,
    color: "var(--c-accent)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  toolbar: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    flexWrap: "wrap",
    padding: "12px 24px",
    borderBottom: "1px solid var(--c-border)",
    background: "var(--c-bg)",
  },
  field: { display: "flex", flexDirection: "column", gap: 4, minWidth: 150 },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--c-text-muted)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  input: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    width: "100%",
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    fontSize: 15,
    minWidth: 150,
  },
  toolbarButtons: { display: "flex", gap: 8, alignItems: "center" },
  applyBtn: {
    padding: "8px 16px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 15,
  },
  resetBtn: {
    padding: "8px 16px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text)",
    cursor: "pointer",
    fontSize: 15,
  },
  notice: { padding: "16px 24px", color: "var(--c-text-dim)" },
  error: { padding: "16px 24px", color: "var(--c-danger)" },
  kpiCard: {
    padding: "14px 16px",
    borderRadius: 16,
    border: "none",
    background: "var(--c-card)",
    boxShadow: "var(--c-shadow-sm)",
  },
  kpiTitle: { fontSize: 13, color: "var(--c-text-muted)", fontWeight: 600 },
  kpiValue: { marginTop: 6, fontSize: 22, fontWeight: 700 },
  kpiHint: { marginTop: 4, fontSize: 12, color: "var(--c-text-dim)" },
  chartCard: {
    border: "none",
    borderRadius: 16,
    background: "var(--c-card)",
    boxShadow: "var(--c-shadow-sm)",
    overflow: "hidden",
  },
  chartTitle: {
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: 600,
    borderBottom: "1px solid var(--c-border)",
  },
  chartBody: { padding: "8px 8px 12px" },
  emptyChart: {
    padding: 48,
    textAlign: "center",
    color: "var(--c-text-dim)",
    fontSize: 15,
  },
  section: { padding: "8px 24px 16px" },
  sectionTitle: { fontSize: 16, fontWeight: 700, marginBottom: 10 },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 16,
    border: "none",
    background: "var(--c-card)",
    boxShadow: "var(--c-shadow-sm)",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 15 },
  th: {
    textAlign: "left",
    padding: "12px",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: "var(--c-text-muted)",
    background: "transparent",
    borderBottom: "1px solid var(--c-border)",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "11px 12px",
    borderBottom: "1px solid var(--c-border)",
    verticalAlign: "middle",
    // Текст переносится, числа — нет: перенос ломает сравнение сумм.
    whiteSpace: "normal",
  },
  empty: { padding: 24, color: "var(--c-text-dim)", fontSize: 15 },
  link: { color: "var(--c-accent)", textDecoration: "none", fontWeight: 600 },
  linkSmall: { color: "var(--c-s1)", textDecoration: "none", fontSize: 14, fontWeight: 700 },
};
